import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Photo } from 'app/types/commonTypes.type';
import type { ListingVideo } from 'app/types/listingDetails.type';
import { MediaCenterTabs } from './MediaCenterTabs';
import { PhotosTab } from './PhotosTab';
import { ThreeDTourTab } from './ThreeDTourTab';
import { VideosTab } from './VideosTab';
import { StreetViewTab } from './StreetViewTab';
import type { MediaCenterTab } from './MediaCenterTabs';

type MediaCenterContentProps = {
  open: boolean;
  photos: Array<Photo>;
  tourUrls: Array<string>;
  tourLabels?: Array<string>;
  has3dTour: boolean;
  hasVideos?: boolean;
  hasStreetView?: boolean;
  videos?: Array<ListingVideo>;
  previewPhoto?: Photo;
  initialTab?: MediaCenterTab;
  headerActions?: ReactNode;
  streetViewLat?: number;
  streetViewLon?: number;
};

export const MediaCenterContent = ({
  open,
  photos,
  tourUrls,
  tourLabels = [],
  has3dTour,
  hasVideos,
  hasStreetView,
  videos = [],
  previewPhoto,
  initialTab = 'photos',
  headerActions,
  streetViewLat,
  streetViewLon,
}: MediaCenterContentProps) => {
  const resolvedInitialTab =
    initialTab === 'streetview' && (streetViewLat == null || streetViewLon == null) ? 'photos' : initialTab;
  const [activeTab, setActiveTab] = useState<MediaCenterTab>(resolvedInitialTab);

  // Reset tab when opening with a different initialTab
  const prevOpenRef = useRef(open);
  if (open && !prevOpenRef.current) {
    setActiveTab(resolvedInitialTab);
  }
  prevOpenRef.current = open;

  return (
    <>
      <MediaCenterTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        has3dTour={has3dTour}
        hasVideos={hasVideos}
        hasStreetView={hasStreetView}
      />
      {activeTab === 'photos' && <PhotosTab photos={photos} headerActions={headerActions} />}
      {activeTab === '3dtour' && (
        <ThreeDTourTab tourUrls={tourUrls} tourLabels={tourLabels} photos={photos} previewPhoto={previewPhoto} />
      )}
      {activeTab === 'videos' && <VideosTab videos={videos} photos={photos} previewPhoto={previewPhoto} />}
      {activeTab === 'streetview' && hasStreetView && streetViewLat != null && streetViewLon != null && (
        <StreetViewTab lat={streetViewLat} lon={streetViewLon} />
      )}
    </>
  );
};
